import {createDrawingCore} from '../../components/editors/drawingCore';

export const MATERIAL_ANNOTATION_VERSION=1;
export const materialInkLimits={maxPoints:300000,maxStrokes:30000,maxStrokePoints:10000,maxHeight:300000};
export const materialInk=createDrawingCore(materialInkLimits);
const empty=()=>({version:MATERIAL_ANNOTATION_VERSION,sources:{}});
const record=value=>value && typeof value==='object' && !Array.isArray(value);
const invalid=()=>new Error('The saved material annotations are invalid.');
const own=(value,key)=>Object.prototype.hasOwnProperty.call(value,key);
const dimension=(value,fallback,max)=>Number.isFinite(Number(value))&&Number(value)>0?Math.min(max,Number(value)):fallback;

// Download tokens and viewing fragments can change without changing the file.
// Generation/version parameters remain: a replacement must get its own ink.
export function canonicalMaterialUrl(value) {
    const source=String(value || '').trim();
    if(!source)return '';
    try{
        const absolute=/^[a-z][a-z\d+.-]*:/i.test(source);
        const url=new URL(source,'https://instil-material.invalid');
        url.hash='';
        [...url.searchParams.keys()].forEach(key=>{
            if(/^(token|alt|signature|expires|googleaccessid|key-pair-id|policy)$/i.test(key) || /^x-(goog|amz)-/i.test(key))url.searchParams.delete(key);
        });
        url.searchParams.sort();
        return absolute?url.href:url.pathname+url.search;
    }catch{return source.split('#')[0];}
}
function sourceIdentity(lecture={}) {
    const type=String(lecture.type || 'image').toLowerCase();
    return JSON.stringify([String(lecture.id || ''),type,canonicalMaterialUrl(lecture.url),String(lecture.materialVersion || lecture.fileVersion || lecture.generation || '')]);
}
function keyForIdentity(identity) {
    let a=2166136261,b=2246822507;
    for(let i=0;i<identity.length;i++){
        const code=identity.charCodeAt(i);
        a=Math.imul(a^code,16777619);b=Math.imul(b^code,3266489909);
    }
    return `material-${(a>>>0).toString(16).padStart(8,'0')}${(b>>>0).toString(16).padStart(8,'0')}`;
}
export const materialSourceKey=lecture=>keyForIdentity(sourceIdentity(lecture));
const sourceFor=lecture=>({key:materialSourceKey(lecture),identity:sourceIdentity(lecture)});
export function materialPageKey(page=1) {
    const key=String(page);
    if(!/^(?:[1-9]\d{0,6}|sheet-[\w-]{1,100})$/.test(key))throw new Error('Choose a valid material page.');
    return key;
}
function normalizePage(page) {
    if(!record(page) || !record(page.drawings) || !Array.isArray(page.drawings.strokes))throw invalid();
    const drawings=materialInk.pack(page.drawings);
    return {width:dimension(page.width,960,20000),height:dimension(page.height,drawings.height || 1280,300000),drawings};
}
export function normalizeMaterialAnnotations(value) {
    if(value==null || (record(value)&&Object.keys(value).length===0))return empty();
    if(!record(value) || value.version!==MATERIAL_ANNOTATION_VERSION || !record(value.sources))throw invalid();
    const sources={};
    Object.entries(value.sources).forEach(([key,entry])=>{
        if(!/^material-[\da-f]{16}$/.test(key) || !record(entry) || typeof entry.source!=='string' || keyForIdentity(entry.source)!==key || !record(entry.pages))throw invalid();
        const pages={};
        Object.entries(entry.pages).forEach(([page,data])=>{pages[materialPageKey(page)]=normalizePage(data);});
        sources[key]={source:entry.source,pages};
    });
    return {version:MATERIAL_ANNOTATION_VERSION,sources};
}
export function getMaterialPageAnnotations(value,lecture,page=1) {
    const {key,identity}=sourceFor(lecture),pageKey=materialPageKey(page);
    if(!value)return null;
    const source=value.sources?.[key];
    if(!source || source.source!==identity || !own(source.pages || {},pageKey))return null;
    return source.pages[pageKey];
}
export function getMaterialPageInk(value,lecture,page=1) {
    return materialInk.normalize(getMaterialPageAnnotations(value,lecture,page)?.drawings);
}
// Immutable page replacement works with drawingCore undo/redo snapshots. An
// empty drawing is kept deliberately so clearing a page survives the next load.
export function setMaterialPageInk(value,lecture,page=1,ink=null,geometry={}) {
    // Loaded state is normalized once by resolveMaterialAnnotations. Preserve
    // other pages by reference: pen input must not repack an entire textbook.
    const state=value==null?empty():value,{key,identity}=sourceFor(lecture),pageKey=materialPageKey(page);
    if(!record(state) || state.version!==MATERIAL_ANNOTATION_VERSION || !record(state.sources))throw invalid();
    const existing=state.sources[key];
    if(existing && existing.source!==identity)throw new Error('This material has a conflicting source identity.');
    const previous=existing?.pages?.[pageKey],drawings=materialInk.pack(ink);
    const next={width:dimension(geometry.width,previous?.width || 960,20000),height:dimension(geometry.height,previous?.height || drawings.height || 1280,300000),drawings};
    return {...state,sources:{...state.sources,[key]:{source:identity,pages:{...(existing?.pages || {}),[pageKey]:next}}}};
}
export function hasMaterialInk(value,lecture) {
    const {key,identity}=sourceFor(lecture),source=value?.sources?.[key];
    return source?.source===identity && Object.values(source.pages || {}).some(page=>page.drawings?.strokes?.length>0);
}
